import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, Instagram, Youtube, Music2, MapPin, Menu, X, Sparkles, SlidersHorizontal, Camera, Briefcase, Route, Mic, Headphones, AudioLines, TrendingUp, Users, Compass } from "lucide-react";
import { Reveal, MaskReveal } from "../components/Reveal";
import { BookingDialog } from "../components/BookingDialog";
import { CastingModal } from "../components/CastingModal";
import { ContactForm } from "../components/ContactForm";
import { Toaster } from "../components/ui/sonner";
import { useLenis } from "../hooks/useLenis";
import {
  NAV,
  FEATURED_ARTIST,
  FOUNDER,
  DARWIN,
  MANIFESTO,
  EVENTS,
  OPPORTUNITY,
  ARTISTS,
  COLLABS,
  VENUES,
  HERO_IMAGE,
} from "../data/canarts";

const Header = ({ onApply }) => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 bg-black/50 backdrop-blur-xl border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="logo-link"
          className="font-display text-2xl md:text-3xl font-bold tracking-tight"
        >
          CAN<span className="text-primary">ARTS</span>
        </a>
        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                data-testid={`nav-${n.href.replace(/\//g, "")}`}
                className="text-sm uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                data-testid={`nav-${n.href.slice(1)}`}
                className="text-sm uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground transition-colors duration-300"
              >
                {n.label}
              </a>
            )
          )}
        </nav>
        <button
          type="button"
          onClick={onApply}
          data-testid="header-apply-cta"
          className="hidden md:inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-medium hover:bg-white hover:text-black transition-colors duration-300"
        >
          Apply Now <ArrowUpRight size={16} />
        </button>
        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-foreground"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-white/10 px-6 py-6 flex flex-col gap-5 bg-black/90">
          {NAV.map((n) =>
            n.route ? (
              <Link
                key={n.href}
                to={n.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-muted-foreground"
              >
                {n.label}
              </Link>
            ) : (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="text-sm uppercase tracking-[0.2em] text-muted-foreground"
              >
                {n.label}
              </a>
            )
          )}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onApply();
            }}
            data-testid="mobile-menu-apply-cta"
            className="inline-flex w-fit items-center gap-2 bg-primary text-primary-foreground px-6 py-2.5 rounded-full text-sm font-medium"
          >
            Apply Now <ArrowUpRight size={16} />
          </button>
        </div>
      )}
    </header>
  );
};

const HERO_SLIDES = [
  {
    key: "casting",
    image:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/w2ccfaw7_ChatGPT%20Image%20Sep%204%2C%202026%2C%2009_42_33%20PM.png",
    alt: "Female artist performing on a Canarts stage",
    eyebrow: "Canarts · Toronto · Artist Discovery",
    lines: ["YOUR STAGE", "STARTS HERE."],
    cta: { label: "Apply for Casting", href: "#contact" },
    duration: 8000,
    body: "casting",
  },
  {
    key: "live",
    image: HERO_IMAGE,
    alt: "Canarts live concert crowd",
    eyebrow: "Canarts · Toronto · Live Music Experiences",
    lines: ["FEEL THE", "LIVE SHOW"],
    cta: { label: "See upcoming events", href: "#events" },
    duration: 7000,
    body: "live",
  },
];

const slideVariants = {
  enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0 }),
};

const HeroSlide = ({ slide, onApply }) => (
  <div
    data-testid={`hero-slide-${slide.key}`}
    className="absolute inset-0 flex flex-col justify-end"
  >
    <img
      src={slide.image}
      alt={slide.alt}
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-black/50" />
    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/20 to-transparent" />

    <div className="relative max-w-[1400px] mx-auto w-full px-6 md:px-10 pb-24 md:pb-28">
      <p
        className="text-sm uppercase tracking-[0.35em] text-secondary mb-6"
        data-testid="hero-eyebrow"
      >
        {slide.eyebrow}
      </p>
      <h1
        data-testid="hero-title"
        className="font-display font-bold tracking-tighter leading-[0.9] text-[13vw] sm:text-[12vw] md:text-[10vw] lg:text-[9rem]"
      >
        {slide.lines.map((l) => (
          <span key={l} className="block">
            {l}
          </span>
        ))}
      </h1>
      <div className="mt-8 flex flex-col md:flex-row md:items-end gap-6 md:justify-between">
        {slide.body === "casting" ? (
          <div className="max-w-md space-y-2">
            <p className="text-base md:text-lg font-light text-muted-foreground">
              Canarts is looking for the next generation of talent.
            </p>
            <p className="text-sm uppercase tracking-[0.2em] text-secondary">
              Singers · Rappers · Musicians · Performers
            </p>
            <p className="text-base md:text-lg font-light text-muted-foreground">
              Open casting is now underway. Show us who you are, what you sound
              like, and why Toronto should know your name.
            </p>
          </div>
        ) : (
          <p className="max-w-md text-base md:text-lg font-light text-muted-foreground">
            We produce live concerts and events that turn artists into legends
            and nights into memories.
          </p>
        )}
        {slide.body === "casting" ? (
          <div className="flex flex-col items-start md:items-end gap-3 shrink-0">
            <button
              type="button"
              onClick={onApply}
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-primary text-white px-8 py-4 md:px-10 md:py-5 rounded-full text-sm md:text-base font-semibold uppercase tracking-[0.15em] shadow-lg shadow-primary/25 hover:brightness-110 hover:scale-[1.03] transition-[transform,filter] duration-300"
            >
              Apply Now
              <ArrowUpRight
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
                size={18}
              />
            </button>
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
              Open Casting · Toronto
            </span>
          </div>
        ) : (
          <a
            href={slide.cta.href}
            data-testid="hero-cta-events"
            className="group inline-flex items-center gap-3 text-sm uppercase tracking-[0.25em] font-medium shrink-0"
          >
            <span className="relative">
              {slide.cta.label}
              <span className="absolute -bottom-1 left-0 h-px w-full bg-primary origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500" />
            </span>
            <ArrowUpRight className="text-primary group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
          </a>
        )}
      </div>
    </div>
  </div>
);

const Hero = ({ onApply }) => {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    const t = setTimeout(() => {
      setDirection(1);
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, HERO_SLIDES[index].duration);
    return () => clearTimeout(t);
  }, [index]);

  const goTo = (i) => {
    setDirection(i > index || (index === HERO_SLIDES.length - 1 && i === 0) ? 1 : -1);
    setIndex(i);
  };
  const next = () => {
    setDirection(1);
    setIndex((i) => (i + 1) % HERO_SLIDES.length);
  };
  const prev = () => {
    setDirection(-1);
    setIndex((i) => (i - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  };

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-background"
    >
      <AnimatePresence initial={false} custom={direction}>
        <motion.div
          key={HERO_SLIDES[index].key}
          custom={direction}
          variants={slideVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
            opacity: { duration: 0.45 },
          }}
          className="absolute inset-0"
        >
          <HeroSlide slide={HERO_SLIDES[index]} onApply={onApply} />
        </motion.div>
      </AnimatePresence>

      {/* Prev / Next */}
      <button
        onClick={prev}
        data-testid="hero-prev"
        aria-label="Previous slide"
        className="absolute z-20 w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300 right-[4.5rem] bottom-20 md:right-auto md:bottom-auto md:left-6 md:top-1/2 md:-translate-y-1/2"
      >
        <ArrowUpRight className="-rotate-[135deg]" size={18} />
      </button>
      <button
        onClick={next}
        data-testid="hero-next"
        aria-label="Next slide"
        className="absolute z-20 w-11 h-11 rounded-full border border-white/20 bg-black/30 backdrop-blur-sm flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300 right-5 bottom-20 md:right-6 md:bottom-auto md:top-1/2 md:-translate-y-1/2"
      >
        <ArrowUpRight className="rotate-45" size={18} />
      </button>

      {/* Indicators */}
      <div className="absolute bottom-8 md:bottom-10 left-1/2 -translate-x-1/2 z-20 flex items-center gap-3">
        {HERO_SLIDES.map((s, i) => (
          <button
            key={s.key}
            onClick={() => goTo(i)}
            data-testid={`hero-indicator-${i}`}
            aria-label={`Go to ${s.key} slide`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === index ? "w-10 bg-primary" : "w-4 bg-white/30 hover:bg-white/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

const MarqueeStrip = () => (
  <div
    data-testid="marquee-strip"
    className="border-y border-white/10 bg-black py-6 md:py-8"
  >
    <Marquee speed={40} gradient={false}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span
          key={i}
          className="font-display text-3xl md:text-5xl font-semibold uppercase tracking-tight mx-8 flex items-center gap-8"
        >
          <span className={i % 2 ? "stroke-text" : ""}>Live The Experience</span>
          <span className="text-primary text-2xl">✳</span>
          <span className={i % 2 ? "" : "stroke-text"}>Canarts Events</span>
          <span className="text-secondary text-2xl">✳</span>
          <span className={i % 2 ? "stroke-text" : ""}>Live The Experience</span>
          <span className="text-primary text-2xl">✳</span>
        </span>
      ))}
    </Marquee>
  </div>
);

const TEAM_ROLES = [
  {
    icon: SlidersHorizontal,
    role: "Music Producers",
    desc: "Crafting beats, arrangements and the sonic identity of every record.",
  },
  {
    icon: Mic,
    role: "Recording Engineers",
    desc: "Capturing every session with pristine, professional sound.",
  },
  {
    icon: AudioLines,
    role: "Mixing Engineers",
    desc: "Balancing and shaping each track to a polished, release-ready mix.",
  },
  {
    icon: Headphones,
    role: "Audio Engineers",
    desc: "Ensuring flawless technical quality across every studio session.",
  },
  {
    icon: Camera,
    role: "Creative & Visual Team",
    desc: "Branding, photography, video and content that bring the vision to life.",
  },
];

const FeaturedArtist = () => {
  const a = FEATURED_ARTIST;
  return (
    <section
      id="featured"
      data-testid="featured-artist"
      className="relative overflow-hidden py-24 md:py-36"
    >
      <div className="spotlight absolute inset-0 pointer-events-none opacity-70" />
      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-5">
            / The People Behind the Sound
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-none mb-5">
            THE CREATIVE <span className="text-primary">TEAM</span>
          </h2>
          <p className="text-sm uppercase tracking-[0.2em] text-secondary mb-6">
            Creative Direction • Music Production • Engineering • Artist Development
          </p>
          <p className="max-w-2xl text-lg font-light text-muted-foreground">
            CANARTS brings together creative direction, music production and audio
            engineering under one team — so artists are supported by a creative
            team, not left to navigate the entire process alone.
          </p>
        </Reveal>

        {/* Creative Director feature */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-16 md:mt-20">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/20 blur-3xl rounded-full" />
              <div className="relative overflow-hidden rounded-3xl border border-white/10 aspect-[4/5] group">
                <img
                  src={a.image}
                  alt={a.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
                <span className="absolute top-5 left-5 text-xs uppercase tracking-[0.25em] bg-primary text-primary-foreground px-4 py-1.5 rounded-full">
                  Creative Director
                </span>
              </div>
            </div>
          </Reveal>

          <div>
            <Reveal>
              <h3 className="font-display text-4xl md:text-6xl font-bold tracking-tight leading-none mb-4">
                {a.name}
              </h3>
              <p className="text-sm uppercase tracking-[0.25em] text-primary mb-8">
                {a.tagline} · {a.handle}
              </p>
              <p className="text-lg md:text-xl font-light text-muted-foreground max-w-xl mb-5">
                {a.body}
              </p>
              <p className="text-base md:text-lg font-light text-muted-foreground max-w-xl mb-5">
                {a.body2}
              </p>
              <p className="text-sm font-light text-muted-foreground/80 max-w-xl mb-8 italic">
                {a.musical}
              </p>
            </Reveal>

            <div className="flex flex-wrap gap-3 mb-10">
              {a.tags.map((t, i) => (
                <Reveal key={t} delay={i * 0.06}>
                  <span className="inline-block text-xs uppercase tracking-[0.18em] border border-white/15 text-muted-foreground px-4 py-2 rounded-full">
                    {t}
                  </span>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <a
                href={a.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="featured-instagram"
                className="inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-3.5 rounded-full text-sm font-medium hover:bg-white hover:text-black transition-colors duration-300"
              >
                <Instagram size={18} /> Follow on Instagram
              </a>
            </Reveal>
          </div>
        </div>

        {/* Team grid */}
        <div className="mt-24 md:mt-32">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
              / Producers & Engineers
            </p>
            <h3 className="font-display text-4xl md:text-5xl font-bold tracking-tight mb-12">
              The team behind every project
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {TEAM_ROLES.map((m, i) => (
              <Reveal key={m.role} delay={i * 0.07}>
                <div
                  data-testid={`team-role-${i}`}
                  className="h-full rounded-2xl border border-white/10 overflow-hidden group hover:border-secondary/40 transition-colors duration-300"
                >
                  <div className="relative aspect-[16/10] bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
                    <div className="spotlight absolute inset-0 opacity-40" />
                    <span className="absolute font-display text-5xl font-bold tracking-tighter text-white/[0.05] select-none">
                      CANARTS
                    </span>
                    <div className="relative w-14 h-14 rounded-full bg-secondary/10 text-secondary flex items-center justify-center group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
                      <m.icon size={24} />
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="text-[11px] uppercase tracking-[0.2em] text-secondary mb-2">
                      Team · Photos coming soon
                    </p>
                    <h4 className="font-display text-xl md:text-2xl font-semibold mb-2">
                      {m.role}
                    </h4>
                    <p className="text-sm font-light text-muted-foreground leading-relaxed">
                      {m.desc}
                    </p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const Founder = () => {
  const people = [FOUNDER, DARWIN];
  return (
    <section
      id="founder"
      data-testid="founder-section"
      className="border-y border-white/10 bg-black py-20 md:py-28"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-12 text-center">
            / The Team
          </p>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          {people.map((f, i) => (
            <Reveal key={f.name} delay={i * 0.1}>
              <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6 text-center sm:text-left">
                <div className="relative shrink-0">
                  <div
                    className={`absolute -inset-3 blur-2xl rounded-full ${
                      i === 0 ? "bg-secondary/20" : "bg-primary/20"
                    }`}
                  />
                  <div className="relative overflow-hidden rounded-full border border-white/10 w-40 h-40 md:w-48 md:h-48">
                    <img
                      src={f.image}
                      alt={f.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                <div>
                  <h3 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">
                    {f.name}
                  </h3>
                  <p
                    className={`text-sm uppercase tracking-[0.2em] mb-4 ${
                      i === 0 ? "text-primary" : "text-secondary"
                    }`}
                  >
                    {f.role}
                  </p>
                  <p className="text-base md:text-lg font-light text-muted-foreground">
                    {f.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

const Manifesto = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-40">
    <Reveal>
      <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-16">
        / Why Canarts
      </p>
    </Reveal>
    <div className="space-y-16 md:space-y-24">
      {MANIFESTO.map((m, i) => (
        <Reveal key={m.n} delay={i * 0.05}>
          <div className="grid md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-white/10 pt-10">
            <div className="md:col-span-2 font-display text-5xl md:text-6xl font-light text-primary">
              {m.n}
            </div>
            <h3 className="md:col-span-4 font-display text-3xl md:text-4xl font-semibold leading-tight">
              {m.title}
            </h3>
            <p className="md:col-span-6 text-lg md:text-xl font-light text-muted-foreground md:pl-8">
              {m.body}
            </p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const SERVICES = [
  {
    icon: SlidersHorizontal,
    title: "Music Production",
    sub: "Production • Recording • Mixing • Mastering",
  },
  {
    icon: Sparkles,
    title: "Artist Development",
    sub: "Creative Direction • Artist Identity • Release Strategy",
  },
  {
    icon: Camera,
    title: "Branding & Content",
    sub: "Brand Identity • Social Media • Photography • Video",
  },
  {
    icon: Briefcase,
    title: "Music Business",
    sub: "Distribution • Rights • Monetization • Strategy",
  },
  {
    icon: Route,
    title: "Tour Management",
    sub: "Planning • Logistics • Shows • Artist Support",
  },
];

const BeyondStage = () => (
  <section
    id="services"
    data-testid="services-section"
    className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28"
  >
    <Reveal>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
            / Beyond the Stage
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight">
            Built for Artists.
          </h2>
        </div>
        <p className="max-w-md text-muted-foreground font-light">
          CANARTS goes beyond live events. We help artists develop their sound,
          identity and career through music production, creative direction and
          music business support.
        </p>
      </div>
    </Reveal>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5">
      {SERVICES.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.06}>
          <div
            data-testid={`service-card-${i}`}
            className="group h-full rounded-xl border border-white/10 p-5 md:p-6 hover:border-secondary/50 hover:-translate-y-1 transition-all duration-300"
          >
            <div className="w-11 h-11 rounded-full bg-secondary/10 text-secondary flex items-center justify-center mb-5 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors duration-300">
              <s.icon size={19} />
            </div>
            <h3 className="font-display text-lg md:text-xl font-semibold mb-2 leading-tight">
              {s.title}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {s.sub}
            </p>
          </div>
        </Reveal>
      ))}
    </div>

    <Reveal delay={0.1}>
      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-5 text-center">
        <p className="text-lg font-light text-muted-foreground">
          Ready to build your next project?
        </p>
        <a
          href="#contact"
          data-testid="work-with-canarts-btn"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium tracking-[0.05em] hover:bg-white hover:text-black transition-colors duration-300"
        >
          Work with CANARTS <ArrowUpRight size={16} />
        </a>
      </div>
    </Reveal>
  </section>
);

const EventCard = ({ ev, onReserve }) => (
  <Reveal
    className={ev.span === "large" ? "md:col-span-8" : "md:col-span-4"}
  >
    <div
      data-testid={`event-card-${ev.id}`}
      className="group relative overflow-hidden rounded-2xl border border-white/10 h-[420px] md:h-[520px] hover:-translate-y-1 transition-transform duration-500"
    >
      {ev.variant === "dj" ? (
        <img
          src={ev.image}
          alt="CANARTS DJ set"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
      ) : (
        <div className="absolute inset-0 bg-[#0a0a0a]">
          <div className="spotlight absolute inset-0 opacity-50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="font-display text-6xl md:text-8xl font-bold tracking-tighter text-white/[0.05] select-none">
              CANARTS
            </span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute top-5 left-5">
        <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] bg-white/10 backdrop-blur-sm text-white px-4 py-1.5 rounded-full">
          <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
          Coming Soon
        </span>
      </div>
      <div className="absolute bottom-0 inset-x-0 p-6 md:p-8">
        <p className="text-xs uppercase tracking-[0.25em] text-secondary mb-2">
          Artists + DJs
        </p>
        <h3 className="font-display text-2xl md:text-3xl font-bold mb-3 leading-tight">
          Featured events <span className="text-primary">TO BE REVEALED</span>
        </h3>
        <div className="flex items-center justify-between gap-3">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={15} /> Location to be announced
          </p>
          <button
            data-testid={`reserve-btn-${ev.id}`}
            onClick={() => onReserve(ev)}
            className="inline-flex items-center gap-1.5 text-sm font-medium bg-white/10 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground px-4 py-3 rounded-full transition-colors duration-300"
          >
            Reserve <ArrowUpRight size={15} />
          </button>
        </div>
      </div>
    </div>
  </Reveal>
);

const Events = () => {
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const reserve = (ev) => {
    setSelected(ev);
    setOpen(true);
  };
  return (
  <section id="events" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
    <Reveal>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
            / Upcoming events
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight">
            Featured Events
          </h2>
        </div>
        <p className="max-w-sm text-muted-foreground font-light">
          Live performances. DJs. One CANARTS experience. Featured artists will
          take the stage first, followed by DJs keeping the night going. Lineups,
          dates and locations will be revealed soon.
        </p>
      </div>
    </Reveal>
    <div className="grid md:grid-cols-12 gap-6 md:gap-8">
      {EVENTS.map((ev) => (
        <EventCard key={ev.id} ev={ev} onReserve={reserve} />
      ))}
    </div>
    <BookingDialog event={selected} open={open} onOpenChange={setOpen} />
  </section>
  );
};

const opportunityIcons = [Mic, TrendingUp, Users, Compass];

const Prizes = ({ onApply }) => (
  <section id="opportunity" className="relative py-24 md:py-32 border-y border-white/10 bg-black overflow-hidden">
    {/* Atmospheric glow: cyan left → pink right */}
    <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
      <div className="absolute -left-40 top-1/4 w-[520px] h-[520px] rounded-full bg-secondary/10 blur-[150px]" />
      <div className="absolute left-1/3 top-1/2 w-[400px] h-[400px] rounded-full bg-secondary/[0.05] blur-[130px]" />
      <div className="absolute -right-40 bottom-0 w-[620px] h-[620px] rounded-full bg-primary/10 blur-[170px]" />
    </div>
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
              / The opportunity
            </p>
            <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight leading-none">
              More than an<br />
              <span className="text-primary">audition</span>
            </h2>
          </div>
          <p className="max-w-sm text-muted-foreground font-light">
            You bring the talent. We help you build what comes next — real
            stages, a real team, real development.
          </p>
        </div>
      </Reveal>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
        {OPPORTUNITY.map((p, i) => {
          const Icon = opportunityIcons[i % opportunityIcons.length];
          return (
            <Reveal key={p.title} delay={i * 0.08}>
              <div
                data-testid={`opportunity-card-${i}`}
                className="group relative h-full overflow-hidden rounded-2xl border border-white/10 p-8 hover:-translate-y-1.5 hover:border-primary/50 hover:shadow-[0_24px_70px_-24px_rgba(255,30,86,0.4)] transition-all duration-500"
              >
                <span
                  aria-hidden="true"
                  className="absolute -top-3 right-3 font-display text-7xl md:text-8xl font-bold tracking-tighter text-white/[0.05] group-hover:text-primary/10 select-none pointer-events-none transition-colors duration-500"
                >
                  0{i + 1}
                </span>
                <div className="relative w-12 h-12 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-primary-foreground group-hover:scale-110 transition-all duration-300">
                  <Icon size={22} />
                </div>
                <h3 className="relative font-display text-xl md:text-2xl font-semibold mb-2">
                  {p.title}
                </h3>
                <p className="relative text-muted-foreground font-light">{p.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={0.2}>
        <div className="mt-14 flex flex-col items-start gap-7">
          <p
            data-testid="opportunity-tagline"
            className="font-display text-xl md:text-3xl font-bold uppercase tracking-[0.08em] leading-tight"
          >
            Talent gets you noticed.
            <br />
            <span className="text-primary">What you build next is what matters.</span>
          </p>
          <button
            type="button"
            onClick={onApply}
            data-testid="opportunity-apply-btn"
            className="group inline-flex items-center gap-3 bg-primary text-white px-8 py-4 md:px-10 md:py-5 rounded-full text-sm md:text-base font-semibold uppercase tracking-[0.15em] shadow-lg shadow-primary/25 hover:brightness-110 hover:scale-[1.03] transition-[transform,filter] duration-300"
          >
            Apply Now
            <ArrowUpRight
              className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
              size={18}
            />
          </button>
        </div>
      </Reveal>
    </div>
  </section>
);

const Artists = () => (
  <section id="artists" className="relative py-24 md:py-32">
    <div className="spotlight absolute inset-0 pointer-events-none" />
    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
          / The lineup
        </p>
        <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight mb-6">
          Artists revealed soon
        </h2>
        <p className="max-w-xl mx-auto text-lg md:text-xl font-light text-muted-foreground">
          We're putting the finishing touches on something special. The full
          artist lineup is kept under wraps for now — stay tuned, it's coming
          soon.
        </p>
        <span className="inline-flex items-center gap-2 mt-8 text-xs uppercase tracking-[0.25em] border border-white/15 text-muted-foreground px-5 py-2.5 rounded-full">
          Coming Soon
        </span>
      </Reveal>
    </div>
  </section>
);

const Collabs = () => (
  <section id="collabs" className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
    <Reveal>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
            / CANARTS Spotlight
          </p>
          <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight">
            Artist's Spotlight of Toronto
          </h2>
        </div>
        <p className="max-w-sm text-muted-foreground font-light">
          A CANARTS selection of artists from Toronto, Ontario and across Canada
          whose sound, creativity and potential have caught people's attention.
        </p>
      </div>
    </Reveal>
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
      {COLLABS.map((c, i) => (
        <Reveal key={c.name} delay={i * 0.08}>
          <div
            data-testid={`collab-card-${i}`}
            className="group relative overflow-hidden rounded-2xl border border-white/10 aspect-[4/5]"
          >
            <img
              src={c.image}
              alt={c.name}
              className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 scale-100 group-hover:scale-105 transition-all duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
            <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] bg-secondary/20 text-secondary backdrop-blur-sm px-3 py-1.5 rounded-full">
              Canarts Pick
            </span>
            <div className="absolute bottom-0 inset-x-0 p-5 md:p-6">
              <p className="text-xs uppercase tracking-[0.25em] text-primary mb-1">
                {c.genre}
              </p>
              <h3 className="font-display text-2xl md:text-3xl font-bold">
                {c.name}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">{c.note}</p>
              <a
                href={c.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`collab-instagram-${i}`}
                className="inline-flex items-center gap-2 text-sm font-medium bg-white/10 backdrop-blur-sm hover:bg-primary hover:text-primary-foreground px-4 py-3 rounded-full transition-colors duration-300"
              >
                <Instagram size={15} /> Profile
              </a>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Gallery = () => (
  <section id="venues" className="py-20 md:py-28">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-12">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
          / Venues
        </p>
        <h2 className="font-display text-5xl md:text-7xl font-bold tracking-tight">
          The Venues
        </h2>
      </Reveal>
    </div>
    <div
      data-testid="venues-scroll"
      className="flex gap-4 md:gap-5 overflow-x-auto px-6 md:px-10 pb-6 snap-x snap-mandatory"
      style={{ scrollbarWidth: "none" }}
    >
      {VENUES.map((src, i) => (
        <div
          key={i}
          className="relative flex-shrink-0 snap-start w-[80%] sm:w-[52%] lg:w-[38%] aspect-[4/3] overflow-hidden rounded-2xl border border-white/10 group"
        >
          <img
            src={src}
            alt={`Canarts venue ${i + 1}`}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <span className="absolute top-4 left-4 font-display text-sm text-white/70">
            0{i + 1}
          </span>
        </div>
      ))}
    </div>
  </section>
);

const SPONSORS = [
  {
    name: "Limon Lounge",
    dark: true,
    logo:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/a105iuhp_ChatGPT%20Image%20Aug%2012%2C%202026%2C%2009_00_16%20PM.png",
  },
  { name: "Coca-Cola",
    logo:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/e3jehyly_ChatGPT%20Image%20Aug%2012%2C%202026%2C%2009_07_43%20PM.webp",
  },
  {
    name: "Michelob ULTRA",
    logo:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/z5wt8q0y_ChatGPT%20Image%20Aug%2012%2C%202026%2C%2009_11_19%20PM.png",
  },
  {
    name: "Pizza Pizza",
    logo:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/ma6sfkir_image.png",
  },
  {
    name: "Tim Hortons",
    logo:
      "https://customer-assets-lxgj4vgw.emergentagent.net/job_event-showcase-117/artifacts/2eydyfd1_ChatGPT%20Image%20Aug%2012%2C%202026%2C%2009_16_39%20PM.webp",
  },
];

const Sponsors = () => (
  <section
    id="partners"
    data-testid="sponsors-section"
    className="border-t border-white/10 py-16 md:py-20"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-4">
          / Our Partners
        </p>
        <h2 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-10">
          Brands Supporting the Experience
        </h2>
      </Reveal>
      <Reveal delay={0.05}>
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
          {SPONSORS.map((s) => (
            <div
              key={s.name}
              data-testid={`sponsor-${s.name.toLowerCase().replace(/\s+/g, "-")}`}
              className={`h-20 md:h-24 w-36 md:w-44 flex items-center justify-center rounded-xl px-5 transition-transform duration-300 hover:-translate-y-1 ${
                s.dark
                  ? "bg-[#0d0d0d] border border-white/15"
                  : "bg-white"
              }`}
            >
              {s.logo ? (
                <img
                  src={s.logo}
                  alt={s.name}
                  className="max-h-12 md:max-h-14 max-w-full object-contain"
                />
              ) : (
                <span className="font-display text-base md:text-lg text-white/70">
                  {s.name}
                </span>
              )}
            </div>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.1}>
        <p className="text-sm text-muted-foreground mt-10 mb-5">
          Want to see your brand here? Partner with CANARTS.
        </p>
        <a
          href="#contact"
          data-testid="become-partner-btn"
          className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3 rounded-full text-sm font-medium tracking-[0.05em] hover:bg-white hover:text-black transition-colors duration-300"
        >
          Become a Partner
        </a>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-12 pt-10 border-t border-white/10 max-w-xl mx-auto">
          <p className="text-xs uppercase tracking-[0.35em] text-secondary mb-3">
            Interested in Investing?
          </p>
          <p className="text-muted-foreground font-light mb-4">
            CANARTS is also open to a limited number of individual investors who
            want to be part of our growth.
          </p>
          <p className="inline-flex items-center gap-2 text-sm text-secondary mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-secondary animate-pulse" />
            A few investment spots remaining.
          </p>
          <div>
            <a
              href="#contact"
              data-testid="invest-btn"
              className="inline-flex items-center gap-2 border border-secondary/50 text-secondary hover:bg-secondary hover:text-secondary-foreground px-7 py-3 rounded-full text-sm font-medium tracking-[0.05em] transition-colors duration-300"
            >
              Invest with CANARTS
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer
    id="contact"
    data-testid="footer-section"
    className="relative border-t border-white/10 pt-24 md:pt-40 pb-12"
  >
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <Reveal>
        <p className="text-sm uppercase tracking-[0.35em] text-secondary mb-8">
          / Join
        </p>
        <h2 className="font-display font-bold tracking-tighter leading-[0.9] text-[16vw] md:text-[11vw] lg:text-[10rem]">
          JOIN THE
          <br />
          <span className="text-primary">CROWD</span>
        </h2>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="mt-14 max-w-2xl">
          <p className="text-lg font-light text-muted-foreground mb-8">
            Booking an artist, planning an event or want to join Canarts Music?
            Drop us a line and we'll reply fast.
          </p>
          <ContactForm />
        </div>
      </Reveal>

      <div className="mt-16 grid md:grid-cols-3 gap-10 border-t border-white/10 pt-12">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-3">
            Contact
          </p>
          <a
            href="mailto:info@canarts.com"
            data-testid="contact-email"
            className="inline-block py-2 -my-2 text-xl md:text-2xl font-display hover:text-primary transition-colors"
          >
            info@canarts.com
          </a>
          <p className="text-muted-foreground mt-2">416-805-2286</p>
        </div>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-3">
            Office
          </p>
          <p className="text-lg font-light">
            600 Bay Street
            <br />
            Toronto, ON · Canada
          </p>
        </div>
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-muted-foreground mb-3">
            Follow us
          </p>
          <div className="flex gap-4">
            <a
              href="https://www.instagram.com/canartsmedia"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="social-link-instagram"
              className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
            >
              <Instagram size={18} />
            </a>
            {[Youtube, Music2].map((Icon, i) => (
              <a
                key={i}
                href="#"
                data-testid={`social-link-${i}`}
                className="w-11 h-11 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors duration-300"
              >
                <Icon size={18} />
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-16 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-muted-foreground">
        <p className="font-display text-lg text-foreground">
          CAN<span className="text-primary">ARTS</span>
        </p>
        <p>© {new Date().getFullYear()} Canarts · Toronto. All rights reserved.</p>
        <Link
          to="/admin"
          data-testid="footer-admin-login"
          className="inline-block py-3 -my-3 text-[11px] uppercase tracking-[0.25em] text-muted-foreground/80 hover:text-primary transition-colors duration-300"
        >
          Admin Login
        </Link>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  useLenis();
  const navigate = useNavigate();
  const [castingOpen, setCastingOpen] = useState(false);

  // Touch devices get the dedicated full-page application (native document scrolling);
  // desktop keeps the modal experience.
  const openApply = () => {
    const isTouch =
      window.matchMedia("(pointer: coarse)").matches || "ontouchstart" in window;
    if (isTouch) {
      navigate("/apply");
    } else {
      setCastingOpen(true);
    }
  };
  useEffect(() => {
    document.documentElement.classList.add("dark");
  }, []);

  return (
    <div className="App relative">
      <div className="grain-overlay" />
      <Toaster position="top-center" theme="dark" richColors />
      <Header onApply={openApply} />
      <main>
        <Hero onApply={openApply} />
        <MarqueeStrip />
        <Collabs />
        <Founder />
        <Manifesto />
        <BeyondStage />
        <Events />
        <Prizes onApply={openApply} />
        <Artists />
        <Gallery />
        <FeaturedArtist />
        <Sponsors />
      </main>
      <Footer />
      <CastingModal open={castingOpen} onClose={() => setCastingOpen(false)} />
    </div>
  );
}
